import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHomePage } from '../components/home/useHomePage';
import ChromeExtensionPromo from '../components/home/ChromeExtensionPromo';
import HeaderButtons from '../components/home/HeaderButtons';
import Footer from '../components/home/Footer';
import { WORLD_EXPLORER_BY_ID, WORLD_EXPLORER_IDS } from '../components/worldExplorer/worldExplorerData';
import { fetchStoriesCatalog } from '../utils/storiesFetcher';
import { getGuidedLessonIdsForWorld } from '../utils/guidedLessonRegistry';
import { useProgressSnapshot, makeLessonKey } from '../utils/progressStore';
import { useRecommendations } from '../utils/recommendations';

// ─── Visual config ────────────────────────────────────────────────────────────

const CHARACTER_CONFIG = {
  pebbles: { emoji: '🐰', bgColor: '#FFF0F5', barColor: '#D4537E' },
  milo:    { emoji: '🦕', bgColor: '#E8FFF8', barColor: '#1D9E75' },
  vector:  { emoji: '🐱', bgColor: '#FFFBEA', barColor: '#BA7517' },
};

const WORLD_ROW_CONFIG = {
  animals:  { bgColor: '#E1F5EE', barColor: '#1D9E75' },
  robotics: { bgColor: '#EEEDFE', barColor: '#534AB7' },
  math:     { bgColor: '#FAEEDA', barColor: '#BA7517' },
  space:    { bgColor: '#E6F1FB', barColor: '#185FA5' },
  pebbles:  { bgColor: '#FBEAF0', barColor: '#D4537E' },
};

const CHARACTER_NAMES = ['pebbles', 'milo', 'vector'];

const HOME_CAROUSEL_BLEED = 18;

const homeCarouselTrackStyle = {
  display: 'flex',
  gap: 12,
  overflowX: 'auto',
  overflowY: 'hidden',
  minWidth: 0,
  maxWidth: '100%',
  paddingBottom: 8,
  marginLeft: -HOME_CAROUSEL_BLEED,
  marginRight: -HOME_CAROUSEL_BLEED,
  paddingLeft: HOME_CAROUSEL_BLEED,
  paddingRight: HOME_CAROUSEL_BLEED,
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
};

const homeCarouselCardStyle = {
  flex: '0 0 min(68vw, 168px)',
  scrollSnapAlign: 'start',
  scrollSnapStop: 'always',
};

function HomeCardCarousel({ ariaLabel, children }) {
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(maxScroll > 4 && el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);

    const onWheel = (event) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      el.removeEventListener('wheel', onWheel);
      ro.disconnect();
    };
  }, [updateScrollState, children]);

  const scrollByPage = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.max(180, el.clientWidth * 0.72);
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  return (
    <div className="home-card-carousel-wrap">
      {canScrollLeft ? (
        <button
          type="button"
          className="home-card-carousel-btn home-card-carousel-btn--prev"
          aria-label="Scroll left"
          onClick={() => scrollByPage(-1)}
        >
          ‹
        </button>
      ) : null}
      {canScrollRight ? (
        <button
          type="button"
          className="home-card-carousel-btn home-card-carousel-btn--next"
          aria-label="Scroll right"
          onClick={() => scrollByPage(1)}
        >
          ›
        </button>
      ) : null}
      <div
        ref={trackRef}
        className="home-card-carousel"
        style={homeCarouselTrackStyle}
        role="list"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Section header row ───────────────────────────────────────────────────────

function SectionHeader({ title, seeAllTo }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--color-text)' }}>{title}</span>
      <Link to={seeAllTo} style={{ fontSize: 13, color: '#534AB7', textDecoration: 'none' }}>
        See all →
      </Link>
    </div>
  );
}

// ─── Worlds section ───────────────────────────────────────────────────────────

function WorldsSection() {
  const progress = useProgressSnapshot();

  const getLessonProgress = (worldId) => {
    const ids = getGuidedLessonIdsForWorld(worldId);
    if (ids.length === 0) return { completed: 0, total: 0 };
    const completed = ids.filter((id) =>
      progress.completedLessons.includes(makeLessonKey(worldId, id)),
    ).length;
    return { completed, total: ids.length };
  };

  return (
    <section style={{ padding: '20px 0 0' }}>
      <SectionHeader title="Explore worlds" seeAllTo="/topics" />
      <HomeCardCarousel ariaLabel="Explore worlds">
        {WORLD_EXPLORER_IDS.map((worldId) => {
          const world = WORLD_EXPLORER_BY_ID[worldId];
          if (!world) return null;
          const config = WORLD_ROW_CONFIG[worldId] ?? { bgColor: 'var(--color-bg-card)', barColor: 'var(--color-primary)' };
          const { completed, total } = getLessonProgress(worldId);
          const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

          return (
            <Link
              key={worldId}
              to={`/topics/${worldId}`}
              role="listitem"
              style={{
                ...homeCarouselCardStyle,
                borderRadius: 12,
                overflow: 'hidden',
                border: '0.5px solid var(--color-border)',
                background: 'var(--color-bg-card)',
                textDecoration: 'none',
                display: 'block',
                color: 'inherit',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 40,
                  background: config.bgColor,
                }}
              >
                {world.icon}
              </div>
              <div style={{ padding: '9px 10px 11px' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text)' }}>
                  {world.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text)', opacity: 0.6, marginTop: 1 }}>
                  {total > 0 ? `${completed} of ${total} lessons` : 'Explore paths'}
                </div>
                <div style={{ height: 3, background: 'var(--color-border)', borderRadius: 2, marginTop: 7 }}>
                  <div
                    style={{
                      height: 3,
                      borderRadius: 2,
                      width: `${pct}%`,
                      background: config.barColor,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            </Link>
          );
        })}
      </HomeCardCarousel>
    </section>
  );
}

// ─── Stories section ──────────────────────────────────────────────────────────

function StoriesSection() {
  const [catalog, setCatalog] = useState(null);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    fetchStoriesCatalog()
      .then((data) => setCatalog(data))
      .catch(() => setFetchError(true));
  }, []);

  const isLoading = catalog === null && !fetchError;

  const findEntry = (name) =>
    catalog?.find((c) => c?.title?.toLowerCase() === name) ?? null;

  return (
    <section style={{ padding: '20px 0 0' }}>
      <SectionHeader title="Stories" seeAllTo="/stories" />
      <HomeCardCarousel ariaLabel="Stories by character">
        {CHARACTER_NAMES.map((name) => {
          const config = CHARACTER_CONFIG[name];
          const displayName = name.charAt(0).toUpperCase() + name.slice(1);

          if (isLoading) {
            return (
              <div
                key={name}
                role="listitem"
                style={{
                  ...homeCarouselCardStyle,
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: '0.5px solid var(--color-border)',
                  background: 'var(--color-bg-card)',
                }}
              >
                <div style={{ width: '100%', height: 120, background: config.bgColor, opacity: 0.45 }} />
                <div style={{ padding: '9px 10px 11px' }}>
                  <div style={{ height: 11, width: '55%', borderRadius: 4, background: 'var(--color-border)', opacity: 0.55 }} />
                  <div style={{ height: 3, borderRadius: 2, background: 'var(--color-border)', marginTop: 18, opacity: 0.4 }} />
                </div>
              </div>
            );
          }

          const entry = findEntry(name);
          const storyCount = entry?.items?.length ?? 0;

          return (
            <Link
              key={name}
              to={`/stories/${name}`}
              role="listitem"
              style={{
                ...homeCarouselCardStyle,
                borderRadius: 12,
                overflow: 'hidden',
                border: '0.5px solid var(--color-border)',
                background: 'var(--color-bg-card)',
                textDecoration: 'none',
                display: 'block',
                color: 'inherit',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 40,
                  background: config.bgColor,
                }}
              >
                {config.emoji}
              </div>
              <div style={{ padding: '9px 10px 11px' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text)' }}>
                  {displayName}
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text)', opacity: 0.6, marginTop: 1 }}>
                  {storyCount > 0 ? `${storyCount} ${storyCount === 1 ? 'story' : 'stories'}` : 'Stories'}
                </div>
                <div style={{ height: 3, background: 'var(--color-border)', borderRadius: 2, marginTop: 7 }}>
                  <div style={{ height: 3, borderRadius: 2, width: '0%', background: config.barColor }} />
                </div>
              </div>
            </Link>
          );
        })}
      </HomeCardCarousel>
    </section>
  );
}

// ─── Pick up where you left off ───────────────────────────────────────────────

function PickUpSection() {
  const nav = useNavigate();
  const { resume } = useRecommendations();
  if (!resume) return null;

  const world = WORLD_EXPLORER_BY_ID[resume.worldId];
  const config = WORLD_ROW_CONFIG[resume.worldId] ?? { bgColor: 'var(--color-bg-card)' };

  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--color-text)',
          opacity: 0.5,
          marginBottom: 8,
        }}
      >
        Pick up where you left off
      </div>
      <button
        type="button"
        onClick={() => nav(`/topics/${resume.worldId}/lesson/${resume.lessonId}`)}
        style={{
          width: '100%',
          background: 'var(--color-bg-card)',
          borderRadius: 12,
          padding: '12px 14px',
          border: '0.5px solid var(--color-border)',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              flexShrink: 0,
              background: config.bgColor,
            }}
          >
            {world?.icon ?? '📚'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text)' }}>
              {resume.lessonTitle}
            </div>
            <div style={{ fontSize: 11, color: 'var(--color-text)', opacity: 0.6, marginTop: 1 }}>
              {resume.worldTitle}
              {resume.estimatedMinutes ? ` · about ${resume.estimatedMinutes} min` : ''}
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#534AB7', flexShrink: 0 }}>
            Continue
          </div>
        </div>
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const pageStyle = {
  width: '100%',
  maxWidth: 1080,
  margin: '0 auto',
  boxSizing: 'border-box',
  padding: '12px 18px 0',
  paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
};

const HomePage = () => {
  const {
    profileOpen,
    user,
    loading,
    profileRef,
    setProfileOpen,
    navigate,
  } = useHomePage();

  return (
    <div style={pageStyle} className="home-page explore-landing">
      <ChromeExtensionPromo />

      <HeaderButtons
        loading={loading}
        user={user}
        profileOpen={profileOpen}
        setProfileOpen={setProfileOpen}
        profileRef={profileRef}
        navigate={navigate}
      />

      <div style={{ paddingTop: 52 }}>
        <div style={{ padding: '20px 0 0' }}>
          <h1 style={{ fontSize: 22, fontWeight: 500, color: 'var(--color-text)', margin: 0, lineHeight: 1.3 }}>
            What do you want to explore today?
          </h1>
          <p style={{ fontSize: 14, color: 'var(--color-text)', opacity: 0.65, margin: '6px 0 0', lineHeight: 1.5 }}>
            Stories and lessons for curious kids.
          </p>
        </div>

        <WorldsSection />

        <div style={{ height: '0.5px', background: 'var(--color-border)', margin: '20px 0 0' }} />

        <StoriesSection />

        <div style={{ height: '0.5px', background: 'var(--color-border)', margin: '20px 0 0' }} />

        <PickUpSection />
      </div>

      <div style={{ width: '100%', marginTop: 28, textAlign: 'center', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <Footer />
      </div>
    </div>
  );
};

export default HomePage;
